import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import Button from './button.gts';
import Popover from './popover.gts';
import type { ButtonIntent, ButtonAppearance, ButtonSize } from './button.gts';
import type { PopoverPlacement } from './popover.gts';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// TooltipButton – reimagined button with integrated hover tooltip
// ---------------------------------------------------------------------------

/**
 * Full component signature for type-only consumers.
 */
export interface TooltipButtonSignature {
  Args: {
    /** The tooltip text to display on hover */
    tooltip: string;
    /** Tooltip placement relative to button @default "top" */
    tooltipPlacement?: PopoverPlacement;
    /** Whether the tooltip is currently disabled (e.g., when a drawer is open) */
    tooltipDisabled?: boolean;
    /** HTML button type */
    type?: 'button' | 'submit' | 'reset';
    /** Visual intent */
    intent?: ButtonIntent;
    /** Appearance variant */
    appearance?: ButtonAppearance;
    /** Size */
    size?: ButtonSize;
    /** Icon component (Phosphor icon etc.) */
    icon?: ComponentLike<{ Args: { size?: number | string }; Element: SVGElement }>;
    /** Icon size override */
    iconSize?: number;
    /** Text label */
    label?: string;
    /** Icon placement */
    iconPlacement?: 'start' | 'end';
    /** Whether button is in active/selected state (shows highlighted background) */
    isActive?: boolean;
    /** Disabled state */
    isDisabled?: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

/**
 * TooltipButton – A button with an integrated hover tooltip.
 *
 * Composes the reimagined Button and Popover components. The tooltip appears
 * on hover via Popover's `@triggerMode="hover"`, with smart viewport-aware
 * positioning and smooth spring-feel animation.
 *
 * Features an `@isActive` state for toolbar/drawer toggle patterns that
 * highlights the button with a tinted background.
 *
 * @example Basic icon-only with tooltip
 * ```gts
 * import TooltipButton from 'tio-reimagined/components/tooltip-button';
 * import { PhFloppyDisk } from 'ember-phosphor-icons';
 *
 * <TooltipButton
 *   @tooltip="Save changes"
 *   @icon={{PhFloppyDisk}}
 *   @appearance="minimal"
 *   {{on "click" this.save}}
 * />
 * ```
 *
 * @example With active state (drawer toggle)
 * ```gts
 * <TooltipButton
 *   @tooltip="Comments"
 *   @isActive={{this.isDrawerOpen}}
 *   @appearance="minimal"
 *   @icon={{PhChat}}
 *   {{on "click" this.toggleDrawer}}
 * />
 * ```
 *
 * @example With label and intent
 * ```gts
 * <TooltipButton
 *   @tooltip="This will permanently remove the item"
 *   @intent="danger"
 *   @icon={{PhTrash}}
 *   @label="Delete"
 *   {{on "click" this.delete}}
 * />
 * ```
 */
export default class TooltipButton extends Component<TooltipButtonSignature> {
  private _hoverCloseTimer: ReturnType<typeof setTimeout> | undefined;

  get placement(): PopoverPlacement {
    return this.args.tooltipPlacement ?? 'top';
  }

  get activeClasses(): string {
    if (!this.args.isActive) return '';

    const intent = this.args.intent ?? 'default';
    switch (intent) {
      case 'primary':
        return 'bg-[#006c9a]/10 dark:bg-[#006c9a]/20';
      case 'danger':
        return 'bg-red-100 dark:bg-red-900/30';
      case 'warning':
        return 'bg-amber-100 dark:bg-amber-900/30';
      case 'success':
        return 'bg-emerald-100 dark:bg-emerald-900/30';
      default:
        return 'bg-gray-100 dark:bg-gray-800';
    }
  }

  // Close the tooltip on click so it doesn't obscure content
  handleClick = (popoverClose: () => void) => {
    popoverClose();
  };

  handleTriggerEnter = (popoverOpen: () => void) => {
    if (this._hoverCloseTimer) {
      clearTimeout(this._hoverCloseTimer);
      this._hoverCloseTimer = undefined;
    }
    popoverOpen();
  };

  handleTriggerLeave = (popoverClose: () => void) => {
    this._hoverCloseTimer = setTimeout(() => popoverClose(), 150);
  };

  <template>
    {{#if @tooltipDisabled}}
      <Button
        @type={{@type}}
        @intent={{@intent}}
        @appearance={{@appearance}}
        @size={{@size}}
        @icon={{@icon}}
        @iconSize={{@iconSize}}
        @label={{@label}}
        @iconPlacement={{@iconPlacement}}
        @isDisabled={{@isDisabled}}
        @ariaLabel={{@tooltip}}
        class={{this.activeClasses}}
        data-test-tooltip-button
        ...attributes
      >
        {{yield}}
      </Button>
    {{else}}
      <Popover
        @placement={{this.placement}}
        @triggerMode="hover"
        @maxWidth={{280}}
        data-test-tooltip-button-popover
      >
        <:trigger as |t|>
          <span
            data-test-tooltip-button-trigger
            class="inline-flex"
            {{t.register}}
            {{on "mouseenter" (fn this.handleTriggerEnter t.open)}}
            {{on "mouseleave" (fn this.handleTriggerLeave t.close)}}
            {{on "focusin" (fn this.handleTriggerEnter t.open)}}
            {{on "focusout" (fn this.handleTriggerLeave t.close)}}
          >
            <Button
              @type={{@type}}
              @intent={{@intent}}
              @appearance={{@appearance}}
              @size={{@size}}
              @icon={{@icon}}
              @iconSize={{@iconSize}}
              @label={{@label}}
              @iconPlacement={{@iconPlacement}}
              @isDisabled={{@isDisabled}}
              @ariaLabel={{@tooltip}}
              class={{this.activeClasses}}
              {{on "click" (fn this.handleClick t.close)}}
              data-test-tooltip-button
              ...attributes
            >
              {{yield}}
            </Button>
          </span>
        </:trigger>
        <:body>
          {{@tooltip}}
        </:body>
      </Popover>
    {{/if}}
  </template>
}
